import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiFetch } from '../services/auth'

// Spinner component
function Spinner({ size = 16 }: { size?: number }) {
  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        border: '2px solid transparent',
        borderTop: '2px solid currentColor',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        display: 'inline-block'
      }}
    />
  )
}

// Add CSS animation for spinner
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.textContent = spinnerStyles
  document.head.appendChild(styleSheet)
}

interface FortnoxConnectionStatus {
  isConnected: boolean
  companyName?: string
  lastSync?: string
  error?: string
}

interface FortnoxConnectionProps {
  showAdminBanner?: boolean
  onNavigateToSettings?: () => void
  fortnoxError?: string | null
  onClearFortnoxError?: () => void
  fortnoxSuccess?: boolean
  onClearFortnoxSuccess?: () => void
}

export function FortnoxConnection({ showAdminBanner = false, onNavigateToSettings, fortnoxError, onClearFortnoxError, fortnoxSuccess, onClearFortnoxSuccess }: FortnoxConnectionProps = {}) {
  const { user } = useAuth()
  const [connectionStatus, setConnectionStatus] = useState<FortnoxConnectionStatus>({ isConnected: false })
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleConnectToFortnox = async () => {
    try {
      setIsLoading(true)
      if (!user?.id) {
        return
      }

      window.location.href = '/api/fortnox/auth'
    } catch (error) {
      // console.error('Error initiating Fortnox connection:', error)
      setConnectionStatus(prev => ({ 
        ...prev, 
        error: 'Fel vid anslutning till Fortnox. Försök igen.' 
      }))
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisconnectFromFortnox = async () => {
    try {
      setIsLoading(true)
      const response = await apiFetch('/fortnox/disconnect', { method: 'POST' })

      if (response.ok) {
        setConnectionStatus({ isConnected: false })
      } else {
        throw new Error('Failed to disconnect from Fortnox')
      }
    } catch (error) {
      // console.error('Error disconnecting from Fortnox:', error)
      setConnectionStatus(prev => ({ 
        ...prev, 
        error: 'Fel vid frånkoppling från Fortnox. Försök igen.' 
      }))
    } finally {
      setIsLoading(false)
    }
  }

  const checkConnectionStatus = async () => {
    try {
      setIsCheckingStatus(true)
      const response = await apiFetch('/fortnox/status')

      if (response.ok) {
        const status = await response.json()
        setConnectionStatus(status)
      }
    } catch (error) {
      // console.error('Error checking Fortnox status:', error)
    } finally {
      setIsCheckingStatus(false)
    }
  }

  // Check connection status on component mount
  useEffect(() => {
    checkConnectionStatus()
  }, [])

  return (
    <div style={{
      backgroundColor: 'white',
      padding: isMobile ? '20px' : '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '24px'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        marginBottom: '20px',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '12px' : '0'
      }}>
        <div style={{
          width: isMobile ? '40px' : '48px',
          height: isMobile ? '40px' : '48px',
          backgroundColor: '#00a651',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: isMobile ? '0' : '16px'
        }}>
          <span style={{ color: 'white', fontSize: isMobile ? '18px' : '20px', fontWeight: 'bold' }}>F</span>
        </div>
        <div>
          <h3 style={{ margin: '0 0 4px 0', color: '#1a202c', fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>
            Fortnox Integration
          </h3>
          <p style={{ margin: 0, color: '#64748b', fontSize: isMobile ? '13px' : '14px' }}>
            Systemets anslutning till Fortnox för automatisk datasynkronisering
          </p>
        </div>
      </div>

      {connectionStatus.error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px'
        }}>
          <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>
            {connectionStatus.error}
          </p>
        </div>
      )}

      {fortnoxError && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <p style={{ margin: '0 0 4px 0', color: '#dc2626', fontSize: '14px', fontWeight: '600' }}>
              Fortnox-anslutning misslyckades
            </p>
            <p style={{ margin: 0, color: '#dc2626', fontSize: '13px' }}>
              Fel: {fortnoxError === 'invalid_scope' ? 'Ogiltiga behörigheter' : 
                    fortnoxError === 'access_denied' ? 'Åtkomst nekad' :
                    fortnoxError === 'callback_failed' ? 'Anslutning misslyckades' :
                    fortnoxError}
            </p>
          </div>
          {onClearFortnoxError && (
            <button
              onClick={onClearFortnoxError}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {fortnoxSuccess && (
        <div style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <p style={{ margin: '0 0 4px 0', color: '#16a34a', fontSize: '14px', fontWeight: '600' }}>
              Fortnox-anslutning lyckades!
            </p>
            <p style={{ margin: 0, color: '#16a34a', fontSize: '13px' }}>
              Systemet är nu anslutet till Fortnox.
            </p>
          </div>
          {onClearFortnoxSuccess && (
            <button
              onClick={onClearFortnoxSuccess}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: '1px solid #16a34a',
                borderRadius: '4px',
                color: '#16a34a',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {isCheckingStatus ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center'
        }}>
          <Spinner size={32} />
          <p style={{ 
            margin: '16px 0 0 0', 
            color: '#64748b', 
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Kontrollerar anslutningsstatus...
          </p>
        </div>
      ) : connectionStatus.isConnected ? (
        <div>
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ color: '#16a34a', fontSize: '16px', marginRight: '8px' }}>✓</span>
              <span style={{ color: '#15803d', fontSize: '14px', fontWeight: '600' }}>
                Systemet är anslutet till Fortnox
              </span>
            </div>
            {connectionStatus.companyName && (
              <p style={{ margin: '0 0 8px 24px', color: '#166534', fontSize: '14px' }}>
                Företag: {connectionStatus.companyName}
              </p>
            )}

          </div>

          <div style={{ 
            display: 'flex', 
            gap: isMobile ? '8px' : '12px',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <button
              onClick={checkConnectionStatus}
              disabled={isLoading}
              style={{
                padding: isMobile ? '12px 16px' : '8px 16px',
                backgroundColor: '#f8fafc',
                color: '#475569',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                minHeight: isMobile ? '44px' : 'auto'
              }}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Spinner size={14} />
                  Kontrollerar...
                </span>
              ) : 'Kontrollera status'}
            </button>
            
            {user?.role === 'admin' && (
              <button
                onClick={handleDisconnectFromFortnox}
                disabled={isLoading}
                style={{
                  padding: isMobile ? '12px 16px' : '8px 16px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: isMobile ? '16px' : '14px',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                  minHeight: isMobile ? '44px' : 'auto'
                }}
              >
                {isLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Spinner size={14} />
                    Kopplar från...
                  </span>
                ) : 'Koppla från'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <p style={{ margin: '0 0 8px 0', color: '#92400e', fontSize: '14px', fontWeight: '600' }}>
              Fortnox-integration ej aktiverad
            </p>
            <p style={{ margin: 0, color: '#a16207', fontSize: '13px', lineHeight: '1.4' }}>
              {user?.role === 'admin' 
                ? 'Anslut systemet till Fortnox för att aktivera ekonomiintegration.'
                : 'Kontakta din systemadministratör för att aktivera Fortnox-integration.'
              }
            </p>
          </div>

          {user?.role === 'admin' && !showAdminBanner && (
            <button
              onClick={handleConnectToFortnox}
              disabled={isLoading}
              style={{
                padding: isMobile ? '16px 24px' : '12px 24px',
                backgroundColor: '#00a651',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                transition: 'all 0.2s ease',
                minHeight: isMobile ? '48px' : 'auto'
              }}
              onMouseOver={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = '#008a44'
                }
              }}
              onMouseOut={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = '#00a651'
                }
              }}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Spinner size={16} />
                  Ansluter...
                </span>
              ) : 'Anslut till Fortnox'}
            </button>
          )}

          {user?.role === 'admin' && showAdminBanner && onNavigateToSettings && (
            <button
              onClick={onNavigateToSettings}
              style={{
                width: '100%',
                padding: isMobile ? '20px 24px' : '16px 24px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                minHeight: isMobile ? '52px' : 'auto'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#1e40af'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
            >
              <span>⚙️</span>
              {isMobile ? 'Gå till Inställningar' : 'Gå till Inställningar för att ansluta Fortnox'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
